// T068: Implement markdown textarea with preview

import { useState } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Eye, Edit } from 'lucide-react'

interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  minLength?: number
  maxLength?: number
  disabled?: boolean
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Write your post content here...',
  minLength = 100,
  maxLength = 100000,
  disabled = false,
}: MarkdownEditorProps) {
  const [showPreview, setShowPreview] = useState(false)

  const characterCount = value.length
  const isUnderMinimum = characterCount < minLength
  const isOverMaximum = characterCount > maxLength

  // Simple markdown to HTML converter for preview
  const renderMarkdown = (text: string) => {
    const html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      // Code blocks
      .replace(/```(.*?)```/gs, '<pre class="bg-muted p-4 rounded-lg my-2 overflow-x-auto"><code>$1</code></pre>')
      // Inline code
      .replace(/`(.*?)`/g, '<code class="bg-muted px-1 rounded">$1</code>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
      // Images
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full rounded-lg my-4" />')
      // Blockquotes
      .replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-muted-foreground pl-4 italic my-2">$1</blockquote>')
      // Lists
      .replace(/^\* (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
      // Paragraphs
      .split('\n\n')
      .map((para) => {
        if (
          para.startsWith('<h') ||
          para.startsWith('<pre') ||
          para.startsWith('<blockquote') ||
          para.startsWith('<li') ||
          para.startsWith('<img')
        ) {
          return para
        }
        return `<p class="my-2">${para}</p>`
      })
      .join('')

    return html
  }

  return (
    <div className="space-y-2">
      {/* Editor/Preview Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            type="button"
            variant={!showPreview ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowPreview(false)}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <Button
            type="button"
            variant={showPreview ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowPreview(true)}
          >
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
        </div>

        {/* Character Count */}
        <div className="text-sm">
          <span
            className={
              isOverMaximum
                ? 'text-destructive'
                : isUnderMinimum
                  ? 'text-muted-foreground'
                  : 'text-green-600'
            }
          >
            {characterCount.toLocaleString()}
          </span>
          <span className="text-muted-foreground">
            {' '}
            / {maxLength.toLocaleString()} characters
          </span>
          {isUnderMinimum && (
            <span className="ml-2 text-muted-foreground">
              (min: {minLength.toLocaleString()})
            </span>
          )}
        </div>
      </div>

      {/* Editor or Preview */}
      {!showPreview ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="min-h-[300px] font-mono text-sm"
          maxLength={maxLength}
        />
      ) : (
        <div
          className="min-h-[300px] rounded-md border border-input bg-background p-4"
          dangerouslySetInnerHTML={{
            __html: value ? renderMarkdown(value) : '<p class="text-muted-foreground">Nothing to preview</p>',
          }}
        />
      )}

      {/* Validation Messages */}
      {isUnderMinimum && characterCount > 0 && (
        <p className="text-sm text-muted-foreground">
          Please write at least {(minLength - characterCount).toLocaleString()} more characters.
        </p>
      )}
      {isOverMaximum && (
        <p className="text-sm text-destructive">
          Content exceeds maximum length by {(characterCount - maxLength).toLocaleString()} characters.
        </p>
      )}

      {/* Markdown Help */}
      <details className="text-sm text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground">
          Markdown formatting help
        </summary>
        <div className="mt-2 space-y-1 pl-4">
          <p><code className="bg-muted px-1">**bold**</code> - <strong>bold text</strong></p>
          <p><code className="bg-muted px-1">*italic*</code> - <em>italic text</em></p>
          <p><code className="bg-muted px-1"># Header</code> - Header text</p>
          <p><code className="bg-muted px-1">[text](url)</code> - Link</p>
          <p><code className="bg-muted px-1">![alt](url)</code> - Image</p>
          <p><code className="bg-muted px-1">`code`</code> - Inline code</p>
          <p><code className="bg-muted px-1">```code block```</code> - Code block</p>
          <p><code className="bg-muted px-1">&gt; quote</code> - Blockquote</p>
          <p><code className="bg-muted px-1">- item</code> - List item</p>
        </div>
      </details>
    </div>
  )
}
